import React, { useEffect, useMemo, useState } from 'react';
import {
  getCriticalEventsRange,
  getDemoCriticalEventsRange,
  toWallClockApiIso,
  downloadWebPatientFhirExport,
  downloadWebPatientRawExport,
  generatePatientOneTimeShareUrl,
  generatePatientTimeBoundShareUrl
} from '../../api/database';
import { getPatientInfo } from '../../api/database';
import { getPatientIntake } from '../../api/patientIntake';
import { getAtrialFibrillationDisplayLabel, isAtrialFibrillationEvent } from '../../utils/atrialFibrillationEvents';
import { getFallDisplayLabel, isFallEvent } from '../../utils/fallEvents';

type TimelineEvent = {
  id: string;
  timestamp: string;
  type: string;
  severity: 'warning' | 'critical';
};

type TimelineTabProps = {
  patId: string;
  isDemo: boolean;
  selectedPatientName: string;
  onSelectPatient: () => void;
};

const toInputDateTimeLocal = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  const hours = String(value.getHours()).padStart(2, '0');
  const minutes = String(value.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const rangeFromMonths = (months: number): { start: string; end: string } => {
  const end = new Date();
  const start = new Date(end);
  start.setMonth(start.getMonth() - months);
  return { start: toInputDateTimeLocal(start), end: toInputDateTimeLocal(end) };
};

type DateTimeRange = { startIso: string; endIso: string };
type ExportAction = 'downloadFhir' | 'downloadRaw';
type ShareMode = 'time-bound' | 'one-time';

const TimelineTab: React.FC<TimelineTabProps> = ({ patId, isDemo, selectedPatientName, onSelectPatient }) => {
  const initialRange = rangeFromMonths(1);
  const [activeQuickRange, setActiveQuickRange] = useState<'1m' | '3m' | '6m' | 'custom'>('1m');
  const [searchText, setSearchText] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [appliedStartDate, setAppliedStartDate] = useState(initialRange.start);
  const [appliedEndDate, setAppliedEndDate] = useState(initialRange.end);
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [rawExportLoading, setRawExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [shareMode, setShareMode] = useState<ShareMode>('time-bound');
  const [shareDialogError, setShareDialogError] = useState<string | null>(null);
  const [isDateRangeDialogOpen, setIsDateRangeDialogOpen] = useState(false);
  const [dateRangeDialogAction, setDateRangeDialogAction] = useState<ExportAction | null>(null);
  const [dateRangeDialogStart, setDateRangeDialogStart] = useState(initialRange.start);
  const [dateRangeDialogEnd, setDateRangeDialogEnd] = useState(initialRange.end);
  const [shareDurationMinutesInput, setShareDurationMinutesInput] = useState('60');
  const [shareStartDateInput, setShareStartDateInput] = useState(initialRange.start);
  const [shareEndDateInput, setShareEndDateInput] = useState(initialRange.end);
  const [shareDescriptionInput, setShareDescriptionInput] = useState('');
  const [shareEndEditedManually, setShareEndEditedManually] = useState(false);
  const [patientDetails, setPatientDetails] = useState<{
    name: string;
    birthday: string;
    email: string;
    phone: string;
  }>({
    name: '',
    birthday: '—',
    email: '—',
    phone: '—',
  });

  useEffect(() => {
    if (!patId) {
      setPatientDetails({ name: '', birthday: '—', email: '—', phone: '—' });
      return;
    }
    let cancelled = false;
    const loadPatientDetails = async () => {
      try {
        const [basicInfo, intake] = await Promise.all([
          getPatientInfo(patId).catch(() => null),
          getPatientIntake(patId).catch(() => null),
        ]);
        if (cancelled) return;
        const intakeDob = intake?.date_of_birth ? new Date(intake.date_of_birth) : null;
        const birthday = intakeDob && !Number.isNaN(intakeDob.getTime()) ? intakeDob.toLocaleDateString() : '—';
        const fallbackName = [basicInfo?.first_name, basicInfo?.last_name].filter(Boolean).join(' ').trim();
        setPatientDetails({
          name: selectedPatientName || intake?.full_name || fallbackName || `Patient ${patId}`,
          birthday,
          email: intake?.email || basicInfo?.email || '—',
          phone: intake?.phone || '—',
        });
      } catch {
        if (cancelled) return;
        setPatientDetails({
          name: selectedPatientName || `Patient ${patId}`,
          birthday: '—',
          email: '—',
          phone: '—',
        });
      }
    };
    loadPatientDetails();
    return () => { cancelled = true; };
  }, [patId, selectedPatientName]);

  const applySearch = () => {
    setSearchApplied(searchText.trim().toLowerCase());
  };

  const clearSearch = () => {
    setSearchText('');
    setSearchApplied('');
  };

  const applyDateRangeIfValid = (nextStart: string, nextEnd: string) => {
    const parsedStart = new Date(nextStart);
    const parsedEnd = new Date(nextEnd);
    if (Number.isNaN(parsedStart.getTime()) || Number.isNaN(parsedEnd.getTime())) {
      setError('Invalid date-time range. Please check start and end.');
      return;
    }
    if (parsedEnd < parsedStart) {
      setError('End date-time must be after start date-time.');
      return;
    }
    setError(null);
    setAppliedStartDate(nextStart);
    setAppliedEndDate(nextEnd);
    setActiveQuickRange('custom');
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    applyDateRangeIfValid(value, endDate);
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
    applyDateRangeIfValid(startDate, value);
  };

  const applyQuickRange = (months: number) => {
    const nextRange = rangeFromMonths(months);
    setStartDate(nextRange.start);
    setEndDate(nextRange.end);
    setAppliedStartDate(nextRange.start);
    setAppliedEndDate(nextRange.end);
    setActiveQuickRange(months === 1 ? '1m' : months === 3 ? '3m' : '6m');
  };

  const downloadPatientData = async () => {
    if (!patId || exportLoading) return;
    openDateRangeDialog('downloadFhir');
  };

  const downloadRawPatientData = async () => {
    if (!patId || rawExportLoading) return;
    openDateRangeDialog('downloadRaw');
  };

  const openShareDialog = (mode: ShareMode) => {
    if (!patId || shareLoading) return;
    const defaultDurationMinutes = 60;
    const now = new Date();
    const defaultEnd = mode === 'one-time'
      ? now
      : new Date(now.getTime() + defaultDurationMinutes * 60 * 1000);
    setShareDurationMinutesInput(String(defaultDurationMinutes));
    setShareStartDateInput(startDate);
    setShareEndDateInput(toInputDateTimeLocal(defaultEnd));
    setShareDescriptionInput('');
    setShareMode(mode);
    setShareDialogError(null);
    setShareEndEditedManually(false);
    setIsShareDialogOpen(true);
  };

  const closeShareDialog = () => {
    if (shareLoading) return;
    setShareDialogError(null);
    setIsShareDialogOpen(false);
  };

  const createShare = async () => {
    if (!patId || shareLoading) return;
    const targetPatId = String(patId).trim();
    const description = String(shareDescriptionInput || '').trim();
    const durationMinutes = Number(String(shareDurationMinutesInput ?? '').trim());
    const parsedShareStart = new Date(shareStartDateInput);
    const parsedShareEnd = new Date(shareEndDateInput);
    setShareDialogError(null);
    if (!description) {
      setShareDialogError('Enter a description for this shared token.');
      setActionSuccess(null);
      return;
    }
    if (Number.isNaN(parsedShareStart.getTime())) {
      setShareDialogError('Enter a valid share start date-time.');
      setActionSuccess(null);
      return;
    }
    if (Number.isNaN(parsedShareEnd.getTime())) {
      setShareDialogError('Enter a valid share end date-time.');
      setActionSuccess(null);
      return;
    }
    if (parsedShareEnd < parsedShareStart) {
      setShareDialogError('Share end date-time must be after share start date-time.');
      setActionSuccess(null);
      return;
    }
    if (shareMode === 'one-time' && parsedShareEnd.getTime() > Date.now()) {
      setShareDialogError('For one-time share, end date-time cannot be later than now.');
      setActionSuccess(null);
      return;
    }
    if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) {
      setShareDialogError('Enter a valid positive duration in minutes.');
      setActionSuccess(null);
      return;
    }

    const expiresInSeconds = Math.round(durationMinutes * 60);
    setShareLoading(true);
    setShareDialogError(null);
    setActionSuccess(null);
    setShareUrl(null);
    try {
      const result = shareMode === 'one-time'
        ? await generatePatientOneTimeShareUrl(
          targetPatId,
          expiresInSeconds,
          toWallClockApiIso(shareStartDateInput),
          toWallClockApiIso(shareEndDateInput),
          description
        )
        : await generatePatientTimeBoundShareUrl(
          targetPatId,
          expiresInSeconds,
          toWallClockApiIso(shareStartDateInput),
          toWallClockApiIso(shareEndDateInput),
          description
        );
      setShareUrl(result.url);
      setActionSuccess(
        `Share URL generated. Data window: ${new Date(result.start_at).toLocaleString()} - ${new Date(result.end_at).toLocaleString()}. Link expires at ${new Date(result.expires_at).toLocaleString()}.`
      );
      setIsShareDialogOpen(false);
      if (navigator?.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(result.url);
          setActionSuccess(
            `Share URL generated and copied. Data window: ${new Date(result.start_at).toLocaleString()} - ${new Date(result.end_at).toLocaleString()}. Link expires at ${new Date(result.expires_at).toLocaleString()}.`
          );
        } catch {
          // Clipboard permissions vary by browser/context; URL is still shown in UI.
        }
      }
    } catch (shareError) {
      console.error('Error generating share URL:', shareError);
      const message = shareError instanceof Error ? shareError.message : 'Failed to generate share URL.';
      setShareDialogError(message);
    } finally {
      setShareLoading(false);
    }
  };

  const parseDateRangeFromDialog = (): DateTimeRange | null => {
    const parsedStart = new Date(dateRangeDialogStart);
    const parsedEnd = new Date(dateRangeDialogEnd);
    if (Number.isNaN(parsedStart.getTime()) || Number.isNaN(parsedEnd.getTime())) {
      setExportError('Invalid date-time format. Use YYYY-MM-DDTHH:mm');
      setActionSuccess(null);
      return null;
    }
    if (parsedEnd < parsedStart) {
      setExportError('End date-time must be after start date-time.');
      setActionSuccess(null);
      return null;
    }
    return {
      startIso: toWallClockApiIso(dateRangeDialogStart),
      endIso: toWallClockApiIso(dateRangeDialogEnd)
    };
  };

  const openDateRangeDialog = (action: ExportAction) => {
    setDateRangeDialogAction(action);
    setDateRangeDialogStart(appliedStartDate);
    setDateRangeDialogEnd(appliedEndDate);
    setExportError(null);
    setActionSuccess(null);
    setIsDateRangeDialogOpen(true);
  };

  const closeDateRangeDialog = () => {
    if (exportLoading || rawExportLoading) return;
    setIsDateRangeDialogOpen(false);
    setDateRangeDialogAction(null);
  };

  const confirmDateRangeDialog = async () => {
    if (!patId || !dateRangeDialogAction) return;
    const range = parseDateRangeFromDialog();
    if (!range) return;
    setIsDateRangeDialogOpen(false);

    if (dateRangeDialogAction === 'downloadFhir') {
      setExportLoading(true);
      setExportError(null);
      setActionSuccess(null);
      try {
        await downloadWebPatientFhirExport(patId, range.startIso, range.endIso);
      } catch (downloadError) {
        console.error('Error exporting patient data:', downloadError);
        setExportError('Failed to export patient data. Please try again.');
      } finally {
        setExportLoading(false);
      }
      return;
    }

    if (dateRangeDialogAction === 'downloadRaw') {
      setRawExportLoading(true);
      setExportError(null);
      setActionSuccess(null);
      try {
        await downloadWebPatientRawExport(patId, range.startIso, range.endIso);
      } catch (downloadError) {
        console.error('Error exporting raw patient data:', downloadError);
        setExportError('Failed to export raw patient data. Please try again.');
      } finally {
        setRawExportLoading(false);
      }
      return;
    }
  };

  useEffect(() => {
    if (!patId) {
      setEvents([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const loadEvents = async () => {
      try {
        const parsedStart = new Date(appliedStartDate);
        const parsedEnd = new Date(appliedEndDate);
        if (Number.isNaN(parsedStart.getTime()) || Number.isNaN(parsedEnd.getTime())) {
          setError('Invalid date-time range. Please check start and end.');
          setEvents([]);
          return;
        }
        if (parsedEnd < parsedStart) {
          setError('End date-time must be after start date-time.');
          setEvents([]);
          return;
        }
        const startIso = toWallClockApiIso(appliedStartDate);
        const endIso = toWallClockApiIso(appliedEndDate);
        const rows = isDemo
          ? await getDemoCriticalEventsRange(startIso, endIso, patId)
          : await getCriticalEventsRange(startIso, endIso, patId);
        if (cancelled) return;

        const mapped: TimelineEvent[] = (rows || [])
          .flatMap((row) => {
            if (!row?.timestamp || !row?.type) return [];
            const rawType = String(row.type).trim();
            if (!rawType) return [];
            const severity: 'warning' | 'critical' = /medium|stage1|stage2|warning|yellow/i.test(rawType)
              ? 'warning'
              : 'critical';
            const ts = new Date(row.timestamp);
            if (Number.isNaN(ts.getTime())) return [];
            return [{ id: `${ts.toISOString()}-${rawType}`, timestamp: ts.toISOString(), type: rawType, severity }];
          })
          .sort((a, b) => +new Date(b.timestamp) - +new Date(a.timestamp));

        setEvents(mapped);
      } catch (loadError) {
        if (cancelled) return;
        console.error('Error loading timeline events:', loadError);
        setError('Failed to load timeline events. Please try again.');
        setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadEvents();
    return () => { cancelled = true; };
  }, [patId, isDemo, appliedStartDate, appliedEndDate]);

  const filteredEvents = useMemo(() => {
    if (!searchApplied) return events;
    return events.filter((event) => {
      const typeText = event.type.replace(/_/g, ' ').toLowerCase();
      const isoTimestamp = event.timestamp.toLowerCase();
      const dateOnly = event.timestamp.slice(0, 10).toLowerCase();
      const localTimestamp = new Date(event.timestamp).toLocaleString().toLowerCase();
      const localDate = new Date(event.timestamp).toLocaleDateString().toLowerCase();
      return (
        typeText.includes(searchApplied) ||
        event.severity.includes(searchApplied) ||
        isoTimestamp.includes(searchApplied) ||
        dateOnly.includes(searchApplied) ||
        localTimestamp.includes(searchApplied) ||
        localDate.includes(searchApplied)
      );
    });
  }, [events, searchApplied]);

  if (!patId) {
    return (
      <div className="ph-card" style={{ padding: 18 }}>
        <p className="ph-muted" style={{ marginBottom: 12 }}>
          Select a patient to view warnings and critical events.
        </p>
        <button type="button" className="ph-btn primary" onClick={onSelectPatient}>
          Select patient
        </button>
      </div>
    );
  }

  return (
    <>
      <section className="ph-card ph-content-card" style={{ marginBottom: 14 }}>
        <div style={{ marginBottom: 14 }}>
          <div
            className="ph-export-toolbar"
            style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}
          >
            <p style={{ margin: 0, fontSize: '1.1rem', color: '#111111', fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0 }}>
              Patient: {patientDetails.name}
            </p>
            <div
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 8,
                alignItems: 'center',
                marginLeft: 'auto',
                overflowX: 'auto',
                minWidth: 0,
              }}
            >
              <button
                type="button"
                className="ph-btn primary"
                onClick={downloadPatientData}
                disabled={exportLoading || rawExportLoading}
                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                {exportLoading ? 'Exporting...' : 'Download patient data'}
              </button>
              <button
                type="button"
                className="ph-btn"
                onClick={() => openShareDialog('time-bound')}
                disabled={shareLoading || exportLoading || rawExportLoading}
                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                {shareLoading ? 'Generating share...' : 'Time-Bound Share'}
              </button>
              <button
                type="button"
                className="ph-btn"
                onClick={() => openShareDialog('one-time')}
                disabled={shareLoading || exportLoading || rawExportLoading}
                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                {shareLoading ? 'Generating share...' : 'One-Time Share'}
              </button>
              <button
                type="button"
                className="ph-btn"
                onClick={downloadRawPatientData}
                disabled={rawExportLoading || exportLoading}
                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                {rawExportLoading ? 'Exporting raw...' : 'Download raw data'}
              </button>
            </div>
          </div>
          {exportError ? (
            <p className="ph-muted" style={{ color: '#dc3545', margin: '0 0 10px 0' }}>{exportError}</p>
          ) : null}
          {actionSuccess ? (
            <p className="ph-muted" style={{ color: '#198754', margin: '0 0 10px 0' }}>{actionSuccess}</p>
          ) : null}
          {shareUrl ? (
            <p className="ph-muted" style={{ margin: '0 0 10px 0', wordBreak: 'break-all' }}>
              Share URL: <a href={shareUrl} target="_blank" rel="noopener noreferrer">{shareUrl}</a>
            </p>
          ) : null}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
            <div className="ph-list-item">
              <strong>Birthday:</strong> {patientDetails.birthday}
            </div>
            <div className="ph-list-item">
              <strong>Email:</strong> {patientDetails.email}
            </div>
            <div className="ph-list-item">
              <strong>Phone:</strong> {patientDetails.phone}
            </div>
          </div>
        </div>
      </section>
      {isShareDialogOpen ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
            padding: 16
          }}
          onClick={closeShareDialog}
        >
          <div
            className="ph-card"
            style={{ width: '100%', maxWidth: 520, padding: 18 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 8px 0' }}>
              {shareMode === 'one-time' ? 'Create One-Time Share Link' : 'Create Time-Bound Share Link'}
            </h3>
            <p className="ph-muted" style={{ margin: '0 0 14px 0' }}>
              Add a description, choose the data window, and set how long this link should stay valid.
            </p>
            {shareDialogError ? (
              <p className="ph-muted" style={{ color: '#dc3545', margin: '0 0 12px 0' }}>
                {shareDialogError}
              </p>
            ) : null}
            <div style={{ display: 'grid', gap: 10 }}>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontWeight: 600 }}>Patient ID</span>
                <input
                  type="text"
                  className="ph-input"
                  value={patId}
                  disabled
                  readOnly
                  aria-readonly="true"
                  style={{
                    backgroundColor: '#e2e6ea',
                    color: '#495057',
                    cursor: 'not-allowed',
                    opacity: 1,
                  }}
                />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontWeight: 600 }}>Share duration (minutes)</span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  className="ph-input"
                  value={shareDurationMinutesInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setShareDurationMinutesInput(value);
                    const minutes = Number(String(value).trim());
                    if (!shareEndEditedManually && Number.isFinite(minutes) && minutes > 0) {
                      const nextEnd = new Date(Date.now() + minutes * 60 * 1000);
                      setShareEndDateInput(toInputDateTimeLocal(nextEnd));
                    }
                  }}
                  placeholder="60"
                  disabled={shareLoading}
                />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontWeight: 600 }}>Description</span>
                <textarea
                  className="ph-input"
                  value={shareDescriptionInput}
                  onChange={(e) => setShareDescriptionInput(e.target.value)}
                  placeholder="Reason for sharing this token"
                  rows={3}
                  disabled={shareLoading}
                />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontWeight: 600 }}>Data start date-time</span>
                <input
                  type="datetime-local"
                  className="ph-input"
                  value={shareStartDateInput}
                  onChange={(e) => setShareStartDateInput(e.target.value)}
                  disabled={shareLoading}
                />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontWeight: 600 }}>Data end date-time</span>
                <input
                  type="datetime-local"
                  className="ph-input"
                  value={shareEndDateInput}
                  onChange={(e) => {
                    setShareEndDateInput(e.target.value);
                    setShareEndEditedManually(true);
                  }}
                  max={shareMode === 'one-time' ? toInputDateTimeLocal(new Date()) : undefined}
                  disabled={shareLoading}
                />
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button type="button" className="ph-btn" onClick={closeShareDialog} disabled={shareLoading}>
                Cancel
              </button>
              <button
                type="button"
                className="ph-btn primary"
                onClick={createShare}
                disabled={shareLoading}
              >
                {shareLoading ? 'Generating...' : shareMode === 'one-time' ? 'Generate one-time link' : 'Generate share link'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {isDateRangeDialogOpen ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
            padding: 16
          }}
          onClick={closeDateRangeDialog}
        >
          <div
            className="ph-card"
            style={{ width: '100%', maxWidth: 540, padding: 18 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 8px 0' }}>Select Date-Time Range</h3>
            <p className="ph-muted" style={{ margin: '0 0 14px 0' }}>
              Choose the exact start and end date-time for this action.
            </p>
            <div style={{ display: 'grid', gap: 10 }}>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontWeight: 600 }}>Start date-time</span>
                <input
                  type="datetime-local"
                  className="ph-input"
                  value={dateRangeDialogStart}
                  onChange={(e) => setDateRangeDialogStart(e.target.value)}
                  disabled={exportLoading || rawExportLoading}
                />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontWeight: 600 }}>End date-time</span>
                <input
                  type="datetime-local"
                  className="ph-input"
                  value={dateRangeDialogEnd}
                  onChange={(e) => setDateRangeDialogEnd(e.target.value)}
                  disabled={exportLoading || rawExportLoading}
                />
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button type="button" className="ph-btn" onClick={closeDateRangeDialog} disabled={exportLoading || rawExportLoading}>
                Cancel
              </button>
              <button
                type="button"
                className="ph-btn primary"
                onClick={confirmDateRangeDialog}
                disabled={exportLoading || rawExportLoading}
              >
                Confirm range
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <section className="ph-card ph-content-card" style={{ marginBottom: 14 }}>
        <div className="ph-timeline-toolbar">
          <input
            type="text"
            className="ph-input"
            placeholder="Search by type, severity, date, or timestamp..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applySearch();
              }
            }}
          />
          <button type="button" className="ph-btn" onClick={clearSearch}>Clear</button>
          <button type="button" className="ph-btn primary" onClick={applySearch}>Search</button>
          <input type="datetime-local" className="ph-input" value={startDate} onChange={(e) => handleStartDateChange(e.target.value)} />
          <input type="datetime-local" className="ph-input" value={endDate} onChange={(e) => handleEndDateChange(e.target.value)} />
          <button type="button" className={`ph-btn ${activeQuickRange === '1m' ? 'quick-active' : ''}`} onClick={() => applyQuickRange(1)}>Last month</button>
          <button type="button" className={`ph-btn ${activeQuickRange === '3m' ? 'quick-active' : ''}`} onClick={() => applyQuickRange(3)}>Last 3 months</button>
          <button type="button" className={`ph-btn ${activeQuickRange === '6m' ? 'quick-active' : ''}`} onClick={() => applyQuickRange(6)}>Last 6 months</button>
        </div>
      </section>

      <section className="ph-card ph-content-card">
        {loading ? (
          <p className="ph-muted">Loading timeline events...</p>
        ) : error ? (
          <p className="ph-muted" style={{ color: '#dc3545', marginBottom: 12 }}>{error}</p>
        ) : filteredEvents.length === 0 ? (
          <p className="ph-muted">No warning or critical events found for this range.</p>
        ) : (
          <div className="ph-list">
            {filteredEvents.map((event) => {
              const isAtrialFibrillation = isAtrialFibrillationEvent(event.type);
              return (
              <div
                className="ph-list-item"
                key={event.id}
                style={isAtrialFibrillation ? {
                  backgroundColor: '#f44336',
                  color: '#fff',
                  borderRadius: 8,
                  padding: '10px 12px',
                } : undefined}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                  <strong style={isAtrialFibrillation ? { color: '#111' } : undefined}>
                    {new Date(event.timestamp).toLocaleString()}
                  </strong>
                  <span style={isAtrialFibrillation ? { fontSize: '1.05rem' } : { textTransform: 'capitalize' }}>
                    {isAtrialFibrillation
                      ? getAtrialFibrillationDisplayLabel(event.type)
                      : isFallEvent(event.type)
                        ? getFallDisplayLabel(event.type)
                        : event.type.replace(/_/g, ' ')}
                  </span>
                  <span
                    className={`ph-badge ${event.severity === 'critical' ? 'danger' : 'warning'}`}
                    style={isAtrialFibrillation ? { backgroundColor: '#fff', color: '#f44336' } : undefined}
                  >
                    {event.severity === 'critical' ? 'Critical' : 'Warning'}
                  </span>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
};

export default TimelineTab;
